import { DomainError } from './errors'
import type { Locale } from './vocabulary'

/** Integer euro cents. Money never travels as a float (CLAUDE.md, non-negotiables). */
export type Cents = number

export function isCents(value: unknown): value is Cents {
  return typeof value === 'number' && Number.isSafeInteger(value)
}

export function assertCents(value: number, label = 'amount'): Cents {
  if (!Number.isSafeInteger(value)) {
    throw new DomainError('invalid_amount', `${label} must be a whole number of cents`, { label })
  }
  return value
}

export function sumCents(values: readonly Cents[]): Cents {
  let total = 0
  for (const value of values) total += assertCents(value)
  return assertCents(total, 'sum')
}

export function multiplyCents(unit: Cents, quantity: number): Cents {
  assertCents(unit, 'unit')
  if (!Number.isSafeInteger(quantity)) {
    throw new DomainError('invalid_quantity', 'quantity must be a whole number', { quantity })
  }
  return assertCents(unit * quantity, 'product')
}

/**
 * `amount × bp / 10 000`, where 100 bp = 1 %. Rounds half away from zero, in
 * integer arithmetic, so the same input gives the same cent on every runtime.
 */
export function applyBasisPoints(amount: Cents, bp: number): Cents {
  assertCents(amount)
  if (!Number.isSafeInteger(bp)) {
    throw new DomainError('invalid_rate', 'basis points must be a whole number', { bp })
  }
  const product = amount * bp
  if (!Number.isSafeInteger(product)) {
    throw new DomainError('invalid_amount', 'amount × rate is too large', { amount, bp })
  }
  const sign = product < 0 ? -1 : 1
  const magnitude = Math.abs(product)
  const whole = Math.floor(magnitude / 10_000)
  const remainder = magnitude - whole * 10_000
  const rounded = sign * (whole + (remainder >= 5_000 ? 1 : 0))
  // Avoid -0 leaking into JSON and equality checks.
  return rounded === 0 ? 0 : rounded
}

const formatters = new Map<Locale, Intl.NumberFormat>()

/** Display only. Never parse the result back into money. */
export function formatEuro(amount: Cents, locale: Locale): string {
  assertCents(amount)
  let formatter = formatters.get(locale)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' })
    formatters.set(locale, formatter)
  }
  return formatter.format(amount / 100)
}
